<template>
    <q-table
        :title="title"
        :data="data"
        :columns="columns"
        :pagination="paginationAux"
        :expanded.sync="expanded"
        :rows-per-page-options="[10, 20, 50, 100]"
        :loading="loading"
        row-key="id"
        flat
        card-class="bg-grey-2"
        @request="request"
    >
        <!-- table top -->
        <template v-slot:top>
            <slot name="top" />
        </template>
        <!-- table body -->
        <template v-slot:body="props">
            <!-- invoice row -->
            <q-tr :props="props">
                <!-- columns -->
                <q-td v-for="col in props.cols" :key="col.name" :props="props">
                    <!-- expand button -->
                    <template v-if="col.name === 'expand_btn'">
                        <q-btn
                            flat
                            :icon="props.expand ? 'expand_less' : 'expand_more'"
                            :style="`text-align: ${col.align} !important`"
                            @click="expandRow(props)"
                        />
                    </template>
                    <!-- id column -->
                    <template v-if="col.name == 'id'">
                        {{ props.row.id == null ? ' - ' : props.row.id }}
                    </template>
                    <!-- invoice payment status column -->
                    <template v-if="col.name == 'is_paid'">
                        <span v-if="col.value" class="text-positive">{{
                            $t('invoices.payment_made')
                        }}</span>
                        <span v-else class="text-negative">{{
                            $t('invoices.payment_to_be_made')
                        }}</span>
                    </template>
                    <!-- invoice number column -->
                    <template v-if="col.name == 'invoice_number'">
                        {{ col.value }}
                    </template>
                    <!-- invoice date column -->
                    <template v-if="col.name == 'invoice_date'">
                        {{ formatDate(props.row.invoice_date) }}
                    </template>
                    <!-- items column -->
                    <template v-if="col.name == 'items'">
                        <q-icon name="info" color="blue-grey-7" size="xs">
                            <q-tooltip>
                                <!-- title row -->
                                <div class="row">
                                    <!-- item name -->
                                    <div
                                        class="col-7 q-pr-md text-bold text-left"
                                    >
                                        {{ $t('general.order_item') }}
                                    </div>
                                    <!-- item quantity -->
                                    <div
                                        class="col-2 text-right text-bold q-pr-md"
                                    >
                                        {{ $t('general.quantity') }}
                                    </div>
                                    <!-- item total -->
                                    <div
                                        class="col-3 text-uppercase text-bold text-right"
                                    >
                                        {{ $t('general.total') }}
                                    </div>
                                </div>
                                <ItemsInInvoice
                                    v-for="(item, index) in props.row.documents
                                        .orderItems"
                                    :key="`item-${index}`"
                                    :item="item"
                                    :currency="props.row.currency"
                                    style="min-width: 400px; width: 100%"
                                ></ItemsInInvoice>
                                <ServicesInInvoice
                                    v-for="(item, index) in props.row.documents
                                        .orderServices"
                                    :key="`service-${index}`"
                                    :item="item"
                                    :currency="props.row.currency"
                                    style="min-width: 400px; width: 100%"
                                ></ServicesInInvoice>
                            </q-tooltip>
                        </q-icon>
                    </template>
                    <!-- actions column -->
                    <template v-if="col.name == 'actions'">
                        <q-btn icon="more_vert" flat dense></q-btn>
                        <q-menu
                            anchor="bottom right"
                            self="top right"
                            :offset="[-15, -4]"
                        >
                            <q-list>
                                <!-- create payment -->
                                <q-item
                                    v-if="props.row.is_paid === false"
                                    v-close-popup
                                    clickable
                                    :disable="orderCanceledOrReturned"
                                    @click="newPayment(props.row)"
                                >
                                    <q-item-section class="text-capitalize">
                                        {{ $t('invoices.issue_payment') }}
                                    </q-item-section>

                                    <q-item-section avatar>
                                        <q-icon name="credit_card" />
                                    </q-item-section>
                                </q-item>

                                <q-item
                                    v-close-popup
                                    clickable
                                    @click="downloadFile(props.row)"
                                >
                                    <q-item-section class="text-capitalize">
                                        {{ $t('actions.download') }}
                                    </q-item-section>

                                    <q-item-section avatar>
                                        <q-icon name="download" />
                                    </q-item-section>
                                </q-item>
                            </q-list>
                        </q-menu>
                    </template>
                </q-td>
            </q-tr>
            <!-- expanded row with payments rows -->
            <q-tr
                v-show="props.expand"
                :props="props"
                style="background: #f6f6f6"
            >
                <q-td colspan="100%">
                    <q-table
                        :data="props.row.payments"
                        :columns="paymentColumns"
                        hide-bottom
                        flat
                        :rows-per-page-options="[0]"
                    >
                        <template v-slot:body="payments">
                            <q-tr :props="payments">
                                <q-td
                                    v-for="col in paymentColumns"
                                    :key="col.name"
                                    :props="payments"
                                >
                                    <!-- payment id -->
                                    <template v-if="col.name === 'id'">
                                        {{ payments.row.id }}
                                    </template>
                                    <!-- payment method -->
                                    <template
                                        v-if="col.name === 'paymentMethod'"
                                    >
                                        {{
                                            JSON.parse(
                                                payments.row.paymentMethod.title
                                            )[locale.key]
                                        }}
                                    </template>
                                    <!-- payment status -->
                                    <template
                                        v-if="col.name === 'paymentStatus'"
                                    >
                                        <span
                                            :class="{
                                                'text-negative':
                                                    payments.row.paymentStatus
                                                        .slug === 'declined' ||
                                                    payments.row.paymentStatus
                                                        .slug === 'timeout' ||
                                                    payments.row.paymentStatus
                                                        .slug === 'canceled',
                                                'text-positive':
                                                    payments.row.paymentStatus
                                                        .slug === 'paid',
                                                'text-warning':
                                                    payments.row.paymentStatus
                                                        .slug === 'pending',
                                            }"
                                        >
                                            <q-icon
                                                :name="
                                                    payments.row.paymentStatus
                                                        .slug === 'declined' ||
                                                    payments.row.paymentStatus
                                                        .slug === 'timeout' ||
                                                    payments.row.paymentStatus
                                                        .slug === 'canceled'
                                                        ? 'credit_card_off'
                                                        : payments.row
                                                              .paymentStatus
                                                              .slug === 'paid'
                                                        ? 'credit_score'
                                                        : payments.row
                                                              .paymentStatus
                                                              .slug ===
                                                          'pending'
                                                        ? 'timer'
                                                        : ''
                                                "
                                                style="font-size: 1.5rem"
                                            ></q-icon>
                                            <span
                                                v-html="
                                                    JSON.parse(
                                                        payments.row
                                                            .paymentStatus
                                                            .description
                                                    )[locale.key]
                                                "
                                            ></span>
                                        </span>
                                    </template>
                                    <!-- payment details -->
                                    <template v-if="col.name === 'details'">
                                        <span>{{
                                            new Intl.NumberFormat(
                                                props.row.currency.key,
                                                {
                                                    style: 'currency',
                                                    currency:
                                                        props.row.currency.key,
                                                }
                                            ).format(
                                                JSON.parse(payments.row.details)
                                                    .amount[
                                                    props.row.currency.key
                                                ]
                                            )
                                        }}</span>
                                        <span
                                            v-if="
                                                payments.row.paymentMethod
                                                    .system_slug == 'multicaixa'
                                            "
                                        >
                                            <br />ENT<br />REF<br />
                                        </span>
                                    </template>
                                    <!-- payment date -->
                                    <template
                                        v-if="col.name === 'payment_date'"
                                    >
                                        {{
                                            formatDateAndTime(
                                                payments.row.payment_date
                                            )
                                        }}
                                    </template>
                                    <!-- payment expire date -->
                                    <template v-if="col.name === 'expire_date'">
                                        {{
                                            formatDateAndTime(
                                                payments.row.expire_date
                                            )
                                        }}
                                    </template>
                                </q-td>
                            </q-tr>
                        </template>
                    </q-table>
                </q-td>
            </q-tr>
        </template>
    </q-table>
</template>

<script>
import formatDate from '@/helpers/date-time'
import store from '@/store'
import env from '@/.env.js'
import ItemsInInvoice from '@/components/orders/ItemsInInvoice'
import ServicesInInvoice from '@/components/orders/ServicesInInvoice'
import _ from 'lodash'
import { slugs } from '@/local-shared/mixins/slugs'

export default {
    name: 'InvoicesTableComponent',

    components: {
        ItemsInInvoice,
        ServicesInInvoice,
    },

    props: {
        loading: {
            type: Boolean,
            default: true,
        },
        title: {
            type: String,
            default: null,
        },
        filter: {
            type: Object,
            default: () => {},
        },
        columns: {
            type: Array,
            default: () => [],
        },
        data: {
            type: Array,
            default: () => [],
        },
        pagination: {
            type: Object,
            default: () => {},
        },
        order: {
            type: Object,
            default: () => {},
        },
    },

    data() {
        return {
            selected: [],
            paginationAux: {},
            filterAux: {},
            expanded: [],

            slugs: slugs,
        }
    },

    computed: {
        locale() {
            return store.getters['locale/currentLocale']
        },

        paymentColumns() {
            return [
                {
                    name: 'id',
                    label: this.$t('general.id'),
                    field: 'id',
                    align: 'left',
                    sortable: true,
                },
                {
                    name: 'paymentMethod',
                    label: this.$tc('payment_methods.payment_method', 1),
                    field: 'paymentMethod',
                    align: 'left',
                    sortable: true,
                },
                {
                    name: 'paymentStatus',
                    label: this.$t('invoices.payment_status'),
                    field: 'paymentStatus',
                    align: 'left',
                    sortable: true,
                },
                {
                    name: 'details',
                    label: this.$t('payment_methods.payment_data'),
                    field: 'details',
                    align: 'left',
                    sortable: true,
                },
                {
                    name: 'payment_date',
                    label: this.$t('payment_methods.payment_date'),
                    field: 'payment_date',
                    align: 'left',
                    sortable: true,
                },
                {
                    name: 'expire_date',
                    label: this.$t('payment_methods.payment_expire_date'),
                    field: 'expire_date',
                    align: 'left',
                    sortable: true,
                },
            ]
        },

        orderCanceledOrReturned() {
            return (
                _.isEmpty(this.order) ||
                (this.order.orderStatus &&
                    [
                        this.slugs.ordersStatus.returned,
                        this.slugs.ordersStatus.canceled,
                    ].includes(this.order.orderStatus.slug))
            )
        },
    },

    watch: {
        pagination(value) {
            this.paginationAux = value
        },

        filter(value) {
            this.filterAux = value
        },
    },

    mounted() {
        this.paginationAux = this.pagination
        this.filterAux = this.filter
    },

    methods: {
        request(props) {
            const { page, rowsPerPage, sortBy, descending } = props.pagination
            const filter = props.filter

            this.paginationAux.page = page
            this.paginationAux.rowsPerPage = rowsPerPage
            this.paginationAux.sortBy = sortBy
            this.paginationAux.descending = descending

            this.filterAux = filter

            this.$emit('request', this.paginationAux, this.filterAux)
        },

        async expandRow(props) {
            return (props.expand = !props.expand)
        },

        formatDate(value) {
            return value ? formatDate.fromServerToUxDate(value) : 'N/D'
        },

        formatDateAndTime(value) {
            return value ? formatDate.fromServerToUxDateTime(value) : 'N/D'
        },

        downloadFile(row) {
            window.open(
                env.endpoint_backend + '/documents/' + row?.document_id,
                '_blank'
            )
        },

        newPayment(row) {
            this.$emit('new-payment', row)
        },
    },
}
</script>
